'use client'

import { BuilderBlocks, type BuilderElement } from '@builder.io/react'
import appStoreLogoEn from '@public/images/app_store.svg'
import appStoreLogoFr from '@public/images/app_store_fr.svg'
import googlePlayLogoEn from '@public/images/google_play.svg'
import googlePlayLogoFr from '@public/images/google_play_fr.svg'
import { uniqueId } from 'lodash'
import Image from 'next/image'
import Link from 'next/link'

import { ampli } from '@src/ampli-web'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectCta } from '@src/utils/analytics.util'

import { Container } from '../Container/Container'
import { LocaleSwitcher } from '../LocaleSwitcher/LocaleSwitcher'
import facebookImg from './assets/facebook.svg'
import instagramImg from './assets/instagram.svg'
import faqIcon from './assets/question.svg'
import youtubeImg from './assets/youtube.svg'

interface certImage {
  imageUrl: string
  alt: string
}

interface BigFooterProps {
  builderBlock?: any
  columns: BuilderElement[]
  disclaimers: BuilderElement[]
  appStoreUrl: string
  googlePlayUrl: string
  instagramUrl: string
  facebookUrl: string
  youtubeUrl: string
  faqUrl: string
  faqText: string
  certificateBadges?: certImage[]
}

export const BigFooter = (props: BigFooterProps) => {
  const {
    columns,
    disclaimers,
    builderBlock,
    faqText,
    faqUrl = '',
    appStoreUrl = '',
    facebookUrl = '',
    googlePlayUrl = '',
    instagramUrl = '',
    youtubeUrl = '',
    certificateBadges = [],
  } = props

  const trackSelectCta = useTrackSelectCta()
  const { locale } = useLocale()
  const appStoreLogo: Record<string, any> = { en: appStoreLogoEn, fr: appStoreLogoFr }
  const googlePlayLogo: Record<string, any> = { en: googlePlayLogoEn, fr: googlePlayLogoFr }

  return (
    <footer className="bg-bodi-gray-pearl py-12">
      <Container>
        <LocalizedLink
          href={faqUrl}
          className="lg:hidden"
          onClick={() => {
            trackSelectCta({ 'cta url': faqUrl })
            ampli.flush()
          }}
        >
          <div className="mb-8 flex gap-2 hover:underline">
            <Image src={faqIcon} width={20} height={20} alt="Faq" />
            <p className="text-body-md">{faqText}</p>
          </div>
        </LocalizedLink>

        <div className="mb-14 flex flex-wrap items-center justify-between gap-8 border-b border-bodi-gray-butters pb-14">
          <div className="flex gap-6">
            <Link href={appStoreUrl} className="shrink-0">
              <Image
                className="h-[41px]"
                src={appStoreLogo[locale.lang]}
                width={120}
                height={40}
                alt="App Store"
                onClick={() => {
                  trackSelectCta({ 'cta url': appStoreUrl })
                  ampli.flush()
                }}
              />
            </Link>
            <Link href={googlePlayUrl} className="shrink-0">
              <Image
                className="h-[40px]"
                src={googlePlayLogo[locale.lang]}
                width={135}
                height={40}
                alt="Google Play"
                onClick={() => {
                  trackSelectCta({ 'cta url': googlePlayUrl })
                  ampli.flush()
                }}
              />
            </Link>
          </div>

          <div className="flex gap-6">
            <Link
              href={instagramUrl}
              onClick={() => {
                trackSelectCta({ 'cta url': instagramUrl })
                ampli.flush()
              }}
            >
              <Image src={instagramImg} width={36} height={36} alt="Instagram" />
            </Link>
            <Link
              href={facebookUrl}
              onClick={() => {
                trackSelectCta({ 'cta url': facebookUrl })
                ampli.flush()
              }}
            >
              <Image src={facebookImg} width={36} height={36} alt="Facebook" />
            </Link>
            <Link
              href={youtubeUrl}
              onClick={() => {
                trackSelectCta({ 'cta url': youtubeUrl })
                ampli.flush()
              }}
            >
              <Image src={youtubeImg} width={36} height={36} alt="Youtube" />
            </Link>
          </div>

          <LocalizedLink
            href={faqUrl}
            className="hidden lg:block"
            onClick={() => {
              trackSelectCta({ 'cta url': faqUrl })
              ampli.flush()
            }}
          >
            <div className="flex gap-2 hover:underline">
              <Image src={faqIcon} width={20} height={20} alt="Faq" />
              <p className="text-body-md">{faqText}</p>
            </div>
          </LocalizedLink>
        </div>

        <div className="mb-14 grid grid-cols-2 gap-8 border-b border-bodi-gray-butters pb-14 lg:grid-cols-4">
          {columns?.map((item: any, index) => {
            return (
              <BuilderBlocks
                key={uniqueId()}
                child
                parentElementId={builderBlock?.id}
                dataPath={`component.options.columns.${index}.column`}
                blocks={item?.column}
              />
            )
          })}
        </div>

        <div className="flex">
          {disclaimers?.map((item: any, index) => {
            return (
              <BuilderBlocks
                key={uniqueId()}
                child
                parentElementId={builderBlock?.id}
                dataPath={`component.options.disclaimers.${index}.disclaimer`}
                blocks={item?.disclaimer}
              />
            )
          })}
        </div>

        <div className="mt-4 flex justify-start lg:justify-end">
          <div className="flex h-14 shrink-0 grow-0 flex-row gap-2">
            {certificateBadges?.map((item) => {
              return (
                <Image
                  key={uniqueId()}
                  src={item.imageUrl}
                  width={150}
                  alt={item.alt}
                  height={50}
                  className="object-contain"
                />
              )
            })}
          </div>
        </div>

        <div className="-ml-4 mt-4">
          <LocaleSwitcher />
        </div>
      </Container>
    </footer>
  )
}
